import { useState } from 'react';
import {
  Accordion,
  Button,
  Dialog,
  Tag,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { ChevronDown, Trash2 } from 'lucide-react';
import { useRemoveRdEquipment } from '@/features/quotes/hooks/rd-equipamentos/use-remove-rd-equipment.hook';
import { MODALITY_LABELS } from '@/features/quotes/utils/rd-modality.util';
import { centsToBRL } from '@/features/quotes/utils/rd-units.util';
import type {
  RdCoverageDef,
  RdModalityCoverages,
  RdProposalEquipment,
} from '@/services/quotes/rd-equipamentos/types';
import { toaster } from '@/shared/ui/toaster.component';
import {
  CoverageMatrix,
  type CoverageSaveStatus,
} from './coverage-matrix.component';

type EquipmentCardProps = {
  item: RdProposalEquipment;
  proposalId: string;
  rateGrid: RdModalityCoverages;
  coverages: RdCoverageDef[];
  status?: CoverageSaveStatus;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars -- nome dos parâmetros documenta o contrato da callback
  onStatusChange: (itemId: string, status: CoverageSaveStatus) => void;
};

/**
 * Card de um equipamento já adicionado à proposta (Step 2, fluxo individual),
 * como item de accordion: no cabeçalho, nome, modalidade (`Tag`),
 * `Ano {year} · {centsToBRL(value)}`, as tags de status do autosave de
 * coberturas e um botão de remoção que confirma via `Dialog`; no conteúdo, a
 * matriz de coberturas DESTE item (`CoverageMatrix`, autosave).
 *
 * O botão de remover fica FORA do `Accordion.ItemTrigger`, como irmão dele —
 * o trigger é um `<button>`, e aninhar outro botão dentro produz HTML
 * inválido além de fazer o clique de remover alternar o accordion.
 *
 * Em modo leitura (`disabled`) o formulário de adicionar equipamento não é
 * renderizado pelo step, então este card é a ÚNICA superfície onde as
 * coberturas de uma proposta congelada aparecem: ele fica visível e
 * desabilitado, nunca ausente.
 */
export function EquipmentCard({
  item,
  proposalId,
  rateGrid,
  coverages,
  status,
  disabled = false,
  onStatusChange,
}: EquipmentCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutateAsync: removeEquipment, isPending } = useRemoveRdEquipment({
    proposalId,
  });

  const displayName = item.equipmentName || item.equipmentCode || 'Equipamento';

  async function handleConfirmRemove() {
    try {
      await removeEquipment(item.itemId);
      setConfirmOpen(false);
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível remover o equipamento',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }

  return (
    <Accordion.Item
      value={item.itemId}
      className='rounded-2xl border border-alba-gray-400'
    >
      <div className='flex flex-row items-center gap-2 pr-3 sm:pr-4'>
        <Accordion.ItemTrigger className='flex flex-1 items-center justify-between gap-4 rounded-2xl px-3 py-4 text-left sm:px-4'>
          <div className='flex flex-col gap-1'>
            <Text className='text-base font-bold text-alba-black-500'>
              {displayName}
            </Text>
            <div className='flex flex-wrap items-center gap-2'>
              <Tag.Root size='sm' colorScheme='blue'>
                <Tag.Label>{MODALITY_LABELS[item.modalitySlug]}</Tag.Label>
              </Tag.Root>
              <Text className='text-sm text-alba-gray-500'>
                {[
                  item.brand,
                  item.model,
                  `Ano ${item.year}`,
                  centsToBRL(item.value),
                  item.serialNumber,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
              {status === 'error' && (
                <Tag.Root size='sm' colorScheme='error'>
                  <Tag.Label>Erro ao salvar</Tag.Label>
                </Tag.Root>
              )}
              {status === 'invalid' && (
                <Tag.Root size='sm' colorScheme='warning'>
                  <Tag.Label>Básica obrigatória</Tag.Label>
                </Tag.Root>
              )}
            </div>
          </div>
          <Accordion.ItemIndicator className='transition-transform data-[state=open]:rotate-180'>
            <ChevronDown
              className='h-5 w-5 text-alba-black-400'
              strokeWidth={1.6}
            />
          </Accordion.ItemIndicator>
        </Accordion.ItemTrigger>

        <button
          type='button'
          onClick={() => setConfirmOpen(true)}
          disabled={disabled}
          aria-label='Remover equipamento'
          className='rounded-lg p-2 text-alba-blue-500 transition hover:bg-alba-gray-200'
        >
          <Trash2 className='h-5 w-5' strokeWidth={1.6} />
        </button>
      </div>

      <Accordion.ItemContent className='flex flex-col gap-4 border-t border-alba-gray-200 px-3 pb-5 pt-4 sm:px-5'>
        <CoverageMatrix
          proposalId={proposalId}
          item={item}
          rateGrid={rateGrid}
          coverages={coverages}
          disabled={disabled}
          onStatusChange={onStatusChange}
        />
      </Accordion.ItemContent>

      <Dialog.Root
        open={confirmOpen}
        onOpenChange={details => setConfirmOpen(details.open)}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='mx-4 w-[calc(100%-2rem)] max-w-md sm:mx-0 sm:w-full'>
            <Dialog.Header>
              <Dialog.Title>Remover equipamento</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Text className='text-sm text-alba-gray-600'>
                Deseja remover &quot;{displayName}&quot; desta proposta? Esta
                ação não poderá ser desfeita.
              </Text>
            </Dialog.Body>
            <Dialog.Footer className='flex justify-end gap-3'>
              <Dialog.CloseTrigger asChild>
                <Button variant='outline' disabled={isPending}>
                  Cancelar
                </Button>
              </Dialog.CloseTrigger>
              <Button
                variant='primary'
                loading={isPending}
                disabled={isPending}
                onClick={handleConfirmRemove}
              >
                Remover
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Accordion.Item>
  );
}
